<template>
  <Drawer
    :title="$t('subscription.subscribe')"
    :start-open="drawerOpen"
    controlled
    data-testid="subscription"
    hide-arrow
  >
    <template #badge>
      <div
        v-if="subscriptionDiscounts.subsequent?.value"
        class="relative top-4 ml-5 w-fit rounded-lg bg-[#FDDA25] px-2 py-1"
      >
        <span class="text-sm font-medium">
          {{
            `${subscriptionDiscounts.subsequent.value}${subscriptionDiscounts.subsequent.unit} ${$t('basket.discount')}`
          }}
        </span>
      </div>
    </template>
    <template #title-right>
      <div class="flex items-center gap-4">
        <ProductPrice
          size="md"
          :price="subscriptionPrice"
          :quantity="quantity"
          :applied-reductions="subscriptionPrice?.appliedReductions"
        />
        <InputToggle
          v-model="subscriptionToggleState"
          :disabled="refetching"
          class="flex"
          @update:model-value="toggleSubscriptionStatus"
        />
      </div>
    </template>
    <template #under-title>
      <ProductSubscriptionSavingInfo
        class="mt-2"
        :quantity="quantity"
        :subscription-discounts="subscriptionDiscounts"
        :subsequent-price="subsequentPrice"
      />
    </template>
    <template #content>
      <ProductSubscriptionSelection
        class="!pt-0"
        :product="product"
        :disabled="refetching"
        :variants="[variant]"
        :quantity="quantity"
        :values="subscriptionDefinition"
        @change="changeSubscriptionDefinition"
      >
        <template v-if="slots['tax-extras']" #tax-extras
          ><span class="text-sm text-gray-600"
            ><slot name="tax-extras"></slot></span
        ></template>
      </ProductSubscriptionSelection>
    </template>
  </Drawer>
</template>

<script setup lang="ts">
import type { Product, Variant } from '@scayle/storefront-nuxt'
import { useFimBasket } from '~/composables/useFimBasket'

const emit = defineEmits(['subscription:change', 'subscription:stop'])

const props = defineProps({
  basketItem: {
    type: Object as PropType<FimBasketItem>,
    required: true,
  },
  product: {
    type: Object as PropType<Product>,
    required: true,
  },
  variant: {
    type: Object as PropType<Variant>,
    required: true,
  },
  quantity: {
    type: Number as PropType<number>,
    default: 1,
  },
  subscriptionDiscounts: {
    type: Object as PropType<{
      subsequent: SubscriptionDiscount | undefined
      initial: SubscriptionDiscount | undefined
    }>,
    default: () => ({
      subsequent: undefined,
      initial: undefined,
    }),
  },
})

const { refetching } = useFimBasket({
  with: BASKET_WITH,
})
const { trackRadioButtonClick } = useTrackingEvents()
const { $featureToggle } = useNuxtApp()
const basketItem = toRef(props, 'basketItem')

const {
  subscriptionIntervals,
  subscriptionTerm,
  subscriptionCancellationPolicy,
  subscriptionDiscounts,
  subscriptionPrice,
  subsequentPrice,
} = await useSubscription(
  props.product,
  'subscription',
  [props.variant],
  `product-subscription-${props.product.id}`,
)

const defaultSubscriptionDefinition = computed<SubscriptionChangeData>(() => ({
  pricePromotionKey: 'subscription',
  subscriptionTerm: toValue(subscriptionTerm),
  subscriptionCancellationPolicy: toValue(subscriptionCancellationPolicy),
  selectedInterval: toValue(subscriptionIntervals)[0],
  selectedPreferredDeliveryDate: toValue(
    getPreferredDeliveryDate(getSubscriptionDeliveryDates()),
  ),
}))

const slots = useSlots()
const { isSubscriptionEnabled } = $featureToggle

const hasSubscription = computed(
  () => isSubscriptionEnabled.value && hasSubscriptionDefined(basketItem),
)

const subscriptionDefinition = computed(() =>
  getSubscriptionDefinition(basketItem),
)

const drawerOpen = ref(hasSubscription.value)
const subscriptionToggleState = ref(hasSubscription.value)

const changeSubscriptionDefinition = (data: SubscriptionChangeData) => {
  subscriptionToggleState.value = true
  emit('subscription:change', data)
}

// Changes from one time purchase to subscription and vice versa
const toggleSubscriptionStatus = async (isSubscriptionActive: boolean) => {
  drawerOpen.value = isSubscriptionActive
  if (isSubscriptionActive) {
    emit('subscription:change', defaultSubscriptionDefinition.value)
    trackRadioButtonClick({ id: 'radio_subscription', label: 'Spar-Abo' })
  } else {
    emit('subscription:stop', defaultSubscriptionDefinition.value)
    trackRadioButtonClick({
      id: 'radio_oneTimePurchase',
      label: 'Einmalkauf',
    })
  }
}
</script>
